'use client';
import { FC, useEffect, useState, useMemo } from 'react';
import { AllFbtItems, CurrentItems } from 'services/fbtItems/types';
import FbtTitle from './fbt-title';
import FbtGrid from './fbt-grid';
import {
  DealerPriceAvailabilityResponse,
  SubTotal
} from 'services/dealerPriceAvailability/types';
import { FrequentlyBoughtTogetherMobile } from './frequently-bought-together-mobile';
import cx from 'classnames';
import { useProductDetailPageContext } from '../../context/product-detail';
import { getFBTSubTotal } from 'services/fbtItems/actions';
import { CartResponse } from 'services/cart/types';

interface FbtProps {
  currentItem: CurrentItems;
  allFbtItems: AllFbtItems[];
  partsPriceData: DealerPriceAvailabilityResponse;
  isFrequestBoughtMobile: boolean;
  isCatCorp?: boolean;
  initialSubtotal?: SubTotal;
  contactDealerComp?: React.ReactNode;
  contactDealerBtn?: React.ReactNode;
  itemSummary: ISummary;
  storeLocationId: string;
  cartDetails: CartResponse;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  shouldCallPriceAndAvailability: boolean;
}

const FrequentlyBoughtTogether: FC<FbtProps> = ({
  currentItem,
  allFbtItems,
  partsPriceData,
  isFrequestBoughtMobile = false,
  isCatCorp,
  initialSubtotal,
  contactDealerComp,
  contactDealerBtn,
  itemSummary,
  storeLocationId,
  cartDetails,
  isRTL = false,
  currencyLdFeatureFlag = false,
  shouldCallPriceAndAvailability
}) => {
  let partPrices = [];
  const { currency, priceAndAvailability } = partsPriceData ?? {};

  const selectedItemPriceData = useMemo(() => {
    return (
      priceAndAvailability?.map((item) => {
        return { ...item, isChecked: item.enableAddToCart };
      }) || []
    );
  }, [priceAndAvailability]);

  const { subTotalResponse, isLoading } = initialSubtotal;
  const [subTotal, setSubTotal] = useState(subTotalResponse);
  const [totalLoading, setTotalLoading] = useState(isLoading);
  const [updatedPartsData, setUpdatedPartsData] = useState(
    selectedItemPriceData
  );
  const { quantity } = useProductDetailPageContext();
  const cartId = cartDetails?.orderHeader?.orderId;

  useEffect(() => {
    if (shouldCallPriceAndAvailability && !updatedPartsData) {
      setUpdatedPartsData(selectedItemPriceData);
    }
  }, [shouldCallPriceAndAvailability, updatedPartsData, selectedItemPriceData]);
  const handleCheckboxChange = async (e) => {
    const partsQtyPriceData = updatedPartsData?.map((item) => {
      return {
        ...item,
        quantity:
          item.partNumber === currentItem.partNumber ? quantity : item.quantity
      };
    });
    partPrices = partsQtyPriceData.reduce((acc, cur) => {
      if (cur.partNumber === e.target.id) {
        cur.isChecked = e.detail.checked;
      }
      return [...acc, cur];
    }, []);
    const { subTotalResponse, isLoading } = await getFBTSubTotal({
      currency: currency,
      fbtParts: partPrices
    });
    setSubTotal(subTotalResponse);
    setTotalLoading(isLoading);
    setUpdatedPartsData(partPrices);
  };

  return (
    <>
      <FrequentlyBoughtTogetherMobile
        currentItem={currentItem}
        products={allFbtItems}
        frequestBoughtMobile={isFrequestBoughtMobile}
        currency={currency}
        partsPriceData={updatedPartsData}
        isCatCorp={isCatCorp}
        subTotal={subTotal}
        setSubTotal={setSubTotal}
        totalLoading={totalLoading}
        setTotalLoading={setTotalLoading}
        handleCheckboxChange={handleCheckboxChange}
        contactDealerComp={contactDealerComp}
        contactDealerBtn={contactDealerBtn}
        itemSummary={itemSummary}
        storeLocationId={storeLocationId}
        cartId={cartId}
        cartDetails={cartDetails}
        isRTL={isRTL}
        currencyLdFeatureFlag={currencyLdFeatureFlag}
      />
      <div
        className={cx('cat-u-margin-top-xl d-flex flex-column row-gap-3 ', {
          'd-none d-md-block': isFrequestBoughtMobile
        })}
        id="pdp-fbt-items-container"
      >
        <FbtTitle />
        <FbtGrid
          allFbtItems={allFbtItems}
          currentItem={currentItem}
          currency={currency}
          partsPriceData={updatedPartsData}
          isCatCorp={isCatCorp}
          subTotal={subTotal}
          setSubTotal={setSubTotal}
          totalLoading={totalLoading}
          setTotalLoading={setTotalLoading}
          handleCheckboxChange={handleCheckboxChange}
          itemSummary={itemSummary}
          storeLocationId={storeLocationId}
          cartDetails={cartDetails}
          isRTL={isRTL}
          currencyLdFeatureFlag={currencyLdFeatureFlag}
        >
          {contactDealerComp}
        </FbtGrid>
      </div>
    </>
  );
};

export default FrequentlyBoughtTogether;
